/**
 * Discovery feed scoring — distinct ranking for each mode.
 *
 * iter319 — Near Me logic rewrite.
 *   - GPS-matched ads now register as "nearby" via an expanding radius
 *     (10 → 25 → 50 km). Previously `scoreNearMe` returned `-dist`
 *     (≤0), and `hasNearbyMatch = score > 0` was permanently false for
 *     GPS results — Near Me always fell through to "No nearby ads
 *     found" even when the user was standing next to a listing.
 *   - City-text fallback preserved for legacy ads without lat/lng.
 *   - New return contract exposes `matchRadiusKm`, `nearbyCount`,
 *     `totalLive`, `adsWithCoords` so the UI can pick the right
 *     message + show honest debug info in development.
 *
 * iter315 — Filter uses the canonical `isLiveListing` helper so we
 * never reject the entire marketplace by mistake.
 */

import { filterLiveListings } from './liveListing.js';
import { getCountry, getRegion, getCity } from '../data/locations.js';

// Expanding-radius buckets (km) — the smallest bucket that yields ≥1
// match is what the UI advertises. 50 km is the outermost ring
// because Sudan's secondary cities (Wad Madani, Atbara, Port Sudan)
// are 100+ km apart — beyond 50 km it's no longer "near me".
export const NEAR_ME_RADII_KM = [10, 25, 50];

// ─────────────────────────────────────────────────────────────────────
// Scoring helpers
// ─────────────────────────────────────────────────────────────────────

// Trending: popularity signals
export function scoreTrending(listing) {
  let score = 0;
  score += (listing.views || listing.view_count || listing.viewCount || 0) * 2;
  // Saves / favorites — additive when present.
  score += (listing.save_count || listing.metrics?.saves || 0) * 3;
  // Shares track viral reach (iter312/iter313).
  score += (listing.shares || listing.metrics?.shares || 0) * 4;
  // Direct contact clicks are the strongest "trending" signal.
  score += (listing.metrics?.contact_clicks || 0) * 4;
  if (listing.is_boosted) score += 8;

  const created = new Date(listing.created_at || 0).getTime();
  const ageHours = (Date.now() - created) / 3600000;
  if (ageHours < 24) score += 5;
  else if (ageHours < 72) score += 3;

  return score;
}

// Haversine — kilometers between two lat/lng pairs.
export function getDistanceKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Normalize a city-ish text to lower-case alphanumerics for tolerant
// comparison ("Khartoum, Sudan" vs "khartoum" → match).
function normalizeCity(raw) {
  if (!raw || typeof raw !== 'string') return '';
  return raw.toLowerCase().normalize('NFKD').replace(/[^a-z0-9\u0600-\u06FF]+/g, ' ').trim();
}

function listingCityText(listing) {
  return (
    listing.city
    || listing.area
    || listing.locationLabel
    || (typeof listing.location === 'string' ? listing.location : '')
    || ''
  );
}

// ─────────────────────────────────────────────────────────────────────
// iter322 Phase A refinement — Strict manual-location matching.
//
// When a buyer manually picks a country/region/city/area in the
// LocationPickerSheet, we MUST NOT bleed in fallback ads from other
// countries. This helper performs a tolerant but country-exact match
// against the ad's structured fields (country, countryCode, region,
// city, area) plus its free-form `location`/`locationLabel` strings.
//
// Match policy:
//   1. If ad explicitly declares a country and it disagrees with the
//      selected country → reject.
//   2. If ad has no explicit country, we accept it only when at least
//      one of its tokens (city/area/location) matches a known city
//      or region inside the selected country.
//   3. Region (governorate) selection further narrows to that region's
//      cities. City selection further narrows to that city's name.
//      Area selection requires an `area`/`location` substring match.
//
// The match is case-insensitive and works across Arabic + English
// spellings courtesy of `normalizeCity` (NFKD + diacritic strip).
// ─────────────────────────────────────────────────────────────────────

function tokensFromAd(ad) {
  return [
    ad?.city,
    ad?.area,
    ad?.region,
    ad?.locationLabel,
    typeof ad?.location === 'string' ? ad.location : '',
  ]
    .map(normalizeCity)
    .filter(Boolean);
}

function adDeclaredCountry(ad) {
  // Accept either `country` (name) or `countryCode` (ISO-2). We
  // compare to the selected country's name AND code so callers can
  // store either form on the ad.
  const explicit = ad?.country || ad?.countryCode || ad?.country_code;
  return explicit ? String(explicit).trim() : '';
}

function countryAllNames(countryNode) {
  if (!countryNode) return [];
  return [
    countryNode.countryCode,
    countryNode.countryName,
    countryNode.countryNameAr,
  ].map(normalizeCity).filter(Boolean);
}

function collectCountryCityTokens(countryNode) {
  if (!countryNode) return { regions: [], cities: [], areas: [] };
  const regions = [];
  const cities = [];
  const areas = [];
  for (const r of countryNode.regions || []) {
    if (r.name) regions.push(normalizeCity(r.name));
    if (r.ar)   regions.push(normalizeCity(r.ar));
    for (const c of r.cities || []) {
      if (c.name) cities.push(normalizeCity(c.name));
      if (c.ar)   cities.push(normalizeCity(c.ar));
      for (const a of c.areas || []) {
        if (a) areas.push(normalizeCity(a));
      }
    }
  }
  return { regions, cities, areas };
}

function tokenSetIntersects(tokens, candidates) {
  if (!tokens.length || !candidates.length) return false;
  return tokens.some(t => candidates.some(c => c && (t === c || t.includes(c) || c.includes(t))));
}

export function matchesManualLocation(ad, manualLocation) {
  if (!manualLocation || !manualLocation.country) return true;
  const country = getCountry(manualLocation.country);
  if (!country) return false;

  // (1) Hard country guard — if the ad declares a country, it MUST
  // match the selected one. This is what blocks Sudanese ads from
  // appearing under an Egypt filter (and vice-versa).
  const adCountry = normalizeCity(adDeclaredCountry(ad));
  const countryNames = countryAllNames(country);
  if (adCountry) {
    if (!countryNames.includes(adCountry)) return false;
  }

  const adTokens = tokensFromAd(ad);
  if (!adCountry && !adTokens.length) {
    // Ad has no country, no city, no area — cannot trust strict match.
    return false;
  }

  // (2) Country-cities membership check (only when ad didn't declare
  // a country up front). A Sudan ad with city="Khartoum" passes for
  // Sudan; an Egypt ad with city="Cairo" passes for Egypt.
  if (!adCountry) {
    const { regions, cities, areas } = collectCountryCityTokens(country);
    const inCountry = tokenSetIntersects(adTokens, [...cities, ...regions, ...areas]);
    if (!inCountry) return false;
  }

  // (3) Region narrowing.
  if (manualLocation.region) {
    const region = getRegion(manualLocation.country, manualLocation.region);
    if (!region) return false;
    const regionTokens = [normalizeCity(region.name), normalizeCity(region.ar)].filter(Boolean);
    const regionCityTokens = (region.cities || []).flatMap(c => [normalizeCity(c.name), normalizeCity(c.ar)]).filter(Boolean);
    const adRegion = normalizeCity(ad?.region || '');
    const regionMatch = (adRegion && regionTokens.includes(adRegion))
                       || tokenSetIntersects(adTokens, [...regionTokens, ...regionCityTokens]);
    if (!regionMatch) return false;
  }

  // (4) City narrowing.
  if (manualLocation.city) {
    const cityNode = getCity(manualLocation.country, manualLocation.region, manualLocation.city);
    const cityTokens = [
      normalizeCity(manualLocation.city),
      cityNode?.ar ? normalizeCity(cityNode.ar) : '',
    ].filter(Boolean);
    if (!tokenSetIntersects(adTokens, cityTokens)) return false;
  }

  // (5) Area narrowing — area names live free-form on the ad.
  if (manualLocation.area) {
    const areaToken = normalizeCity(manualLocation.area);
    if (!areaToken) return false;
    const adArea = normalizeCity(ad?.area || '');
    if (adArea && (adArea === areaToken || adArea.includes(areaToken) || areaToken.includes(adArea))) {
      // direct area field hit
    } else if (!tokenSetIntersects(adTokens, [areaToken])) {
      return false;
    }
  }

  return true;
}

// `scoreNearMe` is kept exported for back-compat (it was part of the
// public surface). New callers should use `generateNearMeFeed` which
// returns a rich result object.
//
// Score convention:
//   • +1000          → GPS distance ≤ 10 km
//   • +800           → GPS distance ≤ 25 km
//   • +600           → GPS distance ≤ 50 km
//   • +100           → city-text match (no GPS on either side)
//   • -dist (km)     → has GPS but outside 50 km (we still sort
//                      closest-first so the ranking is sensible)
//   • 0              → no usable location signal
export function scoreNearMe(listing, userLat, userLng, userCity) {
  const adLat = typeof listing.latitude === 'number' ? listing.latitude : null;
  const adLng = typeof listing.longitude === 'number' ? listing.longitude : null;
  const hasGps = typeof userLat === 'number' && typeof userLng === 'number'
              && typeof adLat === 'number' && typeof adLng === 'number';

  if (hasGps) {
    const dist = getDistanceKm(userLat, userLng, adLat, adLng);
    if (dist <= 10) return 1000 - dist;
    if (dist <= 25) return 800 - dist;
    if (dist <= 50) return 600 - dist;
    return -dist; // still sorted closest-first, just not "nearby"
  }

  // City fallback (no GPS or ad has no coords).
  const adCity = normalizeCity(listingCityText(listing));
  const uCity = normalizeCity(userCity);
  if (uCity && adCity && (adCity.includes(uCity) || uCity.includes(adCity))) return 100;
  return 0;
}

// ─────────────────────────────────────────────────────────────────────
// Feed generators
//
// Contract:
//   - Always return ALL live listings the caller passed in (re-ordered
//     by the mode's score). Never short-circuit to an empty array just
//     because no listing matched the strongest signal — the user's
//     spec is "Trending falls back to newest live ads when analytics
//     are empty", and the secondary sort by `created_at` already
//     handles that natively.
//   - Tie-break by `created_at` desc so the fallback is deterministic.
// ─────────────────────────────────────────────────────────────────────

function tieBreakByRecency(a, b) {
  return new Date(b.created_at || 0) - new Date(a.created_at || 0);
}

export function generateTrendingFeed(listings) {
  return filterLiveListings(listings)
    .map(item => ({ ...item, _trendScore: scoreTrending(item) }))
    .sort((a, b) => {
      if (b._trendScore !== a._trendScore) return b._trendScore - a._trendScore;
      return tieBreakByRecency(a, b);
    });
}

/**
 * @param {Array} listings
 * @param {number=} userLat
 * @param {number=} userLng
 * @param {string=} userCity
 * @param {{locationFilterMode?: ('manual-strict'|'gps-nearby'|'none'), manualLocation?: object}} [opts]
 * @returns {{
 *   items: Array,
 *   hasLocation: boolean,
 *   hasNearbyMatch: boolean,
 *   matchRadiusKm: (number|null),
 *   nearbyCount: number,
 *   totalLive: number,
 *   adsWithCoords: number,
 *   locationFilterMode: string,
 *   strictManualEmpty: boolean,
 * }}
 *
 * `hasLocation` lets the UI tell the difference between "user has no
 * location yet → show neutral notice + fallback to live ads" and
 * "user has location but nothing nearby → show 'No nearby ads found'".
 *
 * `matchRadiusKm` is the smallest expanding-radius ring that yielded
 * matches (10/25/50) so the UI can advertise honesty: "ads within
 * 25 km of you". When the only matches came from city-text it is
 * `null`. When nothing matched it is also `null`.
 *
 * iter322 Phase A refinement — When `locationFilterMode === 'manual-strict'`
 * the pool is hard-filtered through `matchesManualLocation()` BEFORE
 * scoring/sorting; ads that don't match the selected country/region/
 * city/area are removed entirely (no fallback bleed). `gps-nearby` and
 * `none` preserve the legacy behaviour.
 */
export function generateNearMeFeed(listings, userLat, userLng, userCity, opts = {}) {
  const { locationFilterMode = 'none', manualLocation = null } = opts || {};
  const live = filterLiveListings(listings);
  const totalLive = live.length;
  const adsWithCoords = live.reduce((n, ad) => (
    (typeof ad.latitude === 'number' && typeof ad.longitude === 'number') ? n + 1 : n
  ), 0);

  const hasGps = typeof userLat === 'number' && typeof userLng === 'number';
  const hasCity = Boolean((userCity || '').trim());
  const hasLocation = hasGps || hasCity || locationFilterMode === 'manual-strict';

  // Strict manual filtering — drop anything that doesn't match the
  // selected country/region/city/area BEFORE scoring. This is what
  // prevents Sudanese ads showing up under an Egypt filter.
  const pool = (locationFilterMode === 'manual-strict' && manualLocation)
    ? live.filter(ad => matchesManualLocation(ad, manualLocation))
    : live;

  const ranked = pool
    .map(item => ({ ...item, _nearScore: scoreNearMe(item, userLat, userLng, userCity) }))
    .sort((a, b) => {
      if (b._nearScore !== a._nearScore) return b._nearScore - a._nearScore;
      return tieBreakByRecency(a, b);
    });

  // Pick the smallest radius bucket that has ≥1 match. We compute the
  // ad-to-user distances directly (cumulative: a 7 km ad counts in
  // r=10 AND would also count in r=25/r=50, but we report the
  // smallest passing ring so the UI can say "within X km" honestly).
  let matchRadiusKm = null;
  let nearbyCount = 0;
  if (hasLocation) {
    if (hasGps && adsWithCoords > 0) {
      const distances = pool
        .filter(ad => typeof ad.latitude === 'number' && typeof ad.longitude === 'number')
        .map(ad => getDistanceKm(userLat, userLng, ad.latitude, ad.longitude));
      for (const r of NEAR_ME_RADII_KM) {
        const count = distances.filter(d => d <= r).length;
        if (count > 0) {
          matchRadiusKm = r;
          nearbyCount = count;
          break;
        }
      }
    }
    // City-only path (no GPS or no ad has coords) → city matches are
    // also "nearby" (radius unknown → null).
    if (nearbyCount === 0) {
      const cityHits = ranked.filter(it => (it._nearScore || 0) === 100).length;
      if (cityHits > 0) {
        nearbyCount = cityHits;
      }
    }
    // In strict-manual mode, every survivor is "nearby" by definition.
    if (locationFilterMode === 'manual-strict' && nearbyCount === 0 && ranked.length > 0) {
      nearbyCount = ranked.length;
    }
  }

  const hasNearbyMatch = nearbyCount > 0;
  const strictManualEmpty = locationFilterMode === 'manual-strict' && ranked.length === 0;

  return {
    items: ranked,
    hasLocation,
    hasNearbyMatch,
    matchRadiusKm,
    nearbyCount,
    totalLive,
    adsWithCoords,
    locationFilterMode,
    strictManualEmpty,
  };
}

// Top Search: keyword relevance
const TRENDING_KEYWORDS_EN = ['iphone', 'samsung', 'laptop', 'car', 'toyota', 'apartment', 'bike', 'generator', 'solar', 'gold', 'dress', 'sofa'];
const TRENDING_KEYWORDS_AR = ['آيفون', 'سامسونج', 'لابتوب', 'سيارة', 'تويوتا', 'شقة', 'دراجة', 'مولد', 'شمسي', 'ذهب', 'فستان', 'كنب'];

export function scoreTopSearch(listing) {
  let score = 0;
  const text = ((listing.title || '') + ' ' + (listing.title_en || '') + ' ' + (listing.title_ar || '')).toLowerCase();

  [...TRENDING_KEYWORDS_EN, ...TRENDING_KEYWORDS_AR].forEach(kw => {
    if (text.includes(kw.toLowerCase())) score += 3;
  });

  // Recency boost
  const created = new Date(listing.created_at || 0).getTime();
  const daysSince = (Date.now() - created) / 86400000;
  if (daysSince < 3) score += 2;

  return score;
}

export function generateTopSearchFeed(listings) {
  return filterLiveListings(listings)
    .map(item => ({ ...item, _topScore: scoreTopSearch(item) }))
    .sort((a, b) => {
      if (b._topScore !== a._topScore) return b._topScore - a._topScore;
      return tieBreakByRecency(a, b);
    });
}
